import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_LANES } from "@/config/resources";
import { CATEGORY_LABELS, STATUS_META, SECTION_META, catLabel, fmtDate, hexA, isOverdue } from "./docConstants";
import { StatusBadge, CategoryChip, VisibilityChips, LinkChips } from "./DocBits";
import RequestDialog from "./RequestDialog";
import { HelpButton } from "@/components/help/HelpButton";

const SectionCard = ({ id, count, active, onClick }) => {
  const m = SECTION_META[id];
  return (
    <button data-testid={`doc-section-${id}`} onClick={onClick}
      className={`text-left rounded-xl border p-3 transition-all min-w-0 ${active ? "ring-2 ring-[#1C4358] border-[#1C4358]" : "border-[#E6EDF2] hover:shadow-[var(--mmm-shadow-md)]"}`}
      style={{ backgroundColor: active ? hexA(m.hex, 0.06) : "#fff" }}>
      <div className="flex items-center justify-between gap-2">
        <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: hexA(m.hex, 0.14) }}>
          {React.createElement(Icons[m.icon] || Icons.File, { size: 16, style: { color: m.hex } })}
        </div>
        <span className="text-[22px] font-bold leading-none" style={{ color: m.hex }} data-testid={`doc-section-count-${id}`}>{count ?? 0}</span>
      </div>
      <div className="mt-1.5 text-[12px] font-semibold text-[#252221] leading-tight">{m.label}</div>
    </button>
  );
};

export default function DocumentCenter() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);

  const section = params.get("section") || "";
  const status = params.get("status") || "";
  const category = params.get("category") || "";
  const service_lane = params.get("service_lane") || "";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const loadSections = useCallback(async () => {
    try { const { data } = await api.get("/doc-center/sections"); setSections(data.sections); } catch { /* */ }
  }, []);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const p = { limit: 300 };
      if (section) p.section = section;
      if (status) p.status = status;
      if (category) p.category = category;
      if (service_lane) p.service_lane = service_lane;
      if (q) p.q = q;
      const { data } = await api.get("/doc-center/documents", { params: p });
      setDocs(data.documents);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [section, status, category, service_lane, q]);

  useEffect(() => { loadSections(); }, [loadSections]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const onDownload = async (d, e) => {
    e.stopPropagation();
    if (!d.has_file) return toast.info("No file uploaded yet.");
    try { await downloadFile(`/doc-center/documents/${d.id}/download`, d.filename || d.title); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const onArchive = async (d, e) => {
    e.stopPropagation();
    try {
      await api.post(`/doc-center/documents/${d.id}/${d.archived ? "restore" : "archive"}`);
      toast.success(d.archived ? "Document restored." : "Document archived.");
      loadDocs(); loadSections();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="pb-12" data-testid="document-center-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#1C4358]">Document Center</h1>
          <p className="text-[13px] text-[#6B7280]">Upload, request, review, share, and track documents by permission.</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton route="/documents" testid="doc-help-button" />
          <Button variant="outline" size="sm" className="h-9 border-[#D6E0E7] text-[#1C4358] gap-1" data-testid="doc-review-nav-btn" onClick={() => navigate("/documents/review")}>
            <Icons.ClipboardCheck size={15} /> Review queue
          </Button>
          <Button variant="outline" size="sm" className="h-9 border-[#D6E0E7] text-[#1C4358] gap-1" data-testid="doc-requests-nav-btn" onClick={() => navigate("/documents/requests")}>
            <Icons.Send size={15} /> Requests
          </Button>
          <Button size="sm" className="h-9 bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid="doc-new-request-btn" onClick={() => setShowRequest(true)}>
            <Icons.Plus size={15} /> Request document
          </Button>
        </div>
      </div>

      {/* Sections */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 mb-4" data-testid="doc-sections">
        {Object.keys(SECTION_META).map((id) => (
          <SectionCard key={id} id={id} count={sections[id]} active={section === id}
            onClick={() => setParam("section", section === id ? "" : id)} />
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="doc-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Icons.Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setParam("q", search)}
            onBlur={() => q !== search && setParam("q", search)}
            placeholder="Search documents…" className="h-9 pl-8 text-[13px]" data-testid="doc-search" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setParam("status", v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[160px] text-[13px]" data-testid="doc-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category || "all"} onValueChange={(v) => setParam("category", v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[170px] text-[13px]" data-testid="doc-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={service_lane || "all"} onValueChange={(v) => setParam("service_lane", v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-[170px] text-[13px]" data-testid="doc-filter-lane"><SelectValue placeholder="Service lane" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All lanes</SelectItem>
            {SERVICE_LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(section || status || category || service_lane || q) && (
          <Button variant="ghost" size="sm" className="h-9 text-[#9C1E22] text-[12px]" data-testid="doc-clear-filters"
            onClick={() => { setSearch(""); setParams({}, { replace: true }); }}>
            <Icons.X size={14} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden">
        <div className="hidden lg:grid grid-cols-[minmax(0,2.4fr)_1fr_1fr_1.4fr_0.9fr_auto] gap-3 px-4 py-2.5 bg-[#F7FAFC] border-b border-[#E6EDF2] text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]">
          <div>Document</div><div>Status</div><div>Visibility</div><div>Linked to</div><div>Updated</div><div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#6B7280] text-sm"><Icons.Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
        ) : !docs.length ? (
          <div className="p-10 text-center text-[#6B7280] text-sm" data-testid="doc-empty">No documents match these filters.</div>
        ) : docs.map((d) => (
          <div key={d.id} data-testid={`doc-row-${d.id}`} onClick={() => navigate(`/documents/${d.id}`)}
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,2.4fr)_1fr_1fr_1.4fr_0.9fr_auto] gap-2 lg:gap-3 px-4 py-3 border-b border-[#EEF2F5] last:border-0 hover:bg-[#F7FAFC] cursor-pointer items-center">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Icons.FileText size={15} className="text-[#1C4358] shrink-0" />
                <span className="text-[13.5px] font-semibold text-[#252221] truncate">{d.title}</span>
                {isOverdue(d) && <span className="text-[10px] font-bold text-[#9C1E22] bg-[#9C1E22]/10 px-1.5 py-0.5 rounded" data-testid={`doc-overdue-${d.id}`}>OVERDUE</span>}
              </div>
              <div className="mt-1 flex items-center gap-2"><CategoryChip category={d.category} />
                {d.uploaded_by_name && <span className="text-[11px] text-[#94A3B8] truncate">by {d.uploaded_by_name}</span>}
              </div>
            </div>
            <div><StatusBadge status={d.status} testid={`doc-status-${d.id}`} /></div>
            <div><VisibilityChips visibility={d.visibility} internalOnly={d.internal_only} /></div>
            <div><LinkChips links={d.links} /></div>
            <div className="text-[12px] text-[#6B7280]">{fmtDate(d.updated_at)}</div>
            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#1C4358]" title="View" data-testid={`doc-view-${d.id}`} onClick={() => navigate(`/documents/${d.id}`)}><Icons.Eye size={15} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#1C4358] disabled:opacity-40" title="Download" disabled={!d.has_file} data-testid={`doc-download-${d.id}`} onClick={(e) => onDownload(d, e)}><Icons.Download size={15} /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#6B7280]" title={d.archived ? "Restore" : "Archive"} data-testid={`doc-archive-${d.id}`} onClick={(e) => onArchive(d, e)}>{d.archived ? <Icons.ArchiveRestore size={15} /> : <Icons.Archive size={15} />}</Button>
            </div>
          </div>
        ))}
      </div>

      <RequestDialog open={showRequest} onOpenChange={setShowRequest} onCreated={() => { loadDocs(); loadSections(); }} />
    </div>
  );
}
